import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brain, Warning, ShieldCheck, Lightning, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { Badge } from "./ui-kit";

const toneMap = {
  critical: "danger",
  high: "danger",
  warning: "warning",
  medium: "warning",
  primary: "primary",
  success: "success",
  low: "default",
  info: "default",
};

const iconMap = {
  critical: Warning,
  high: Warning,
  warning: Warning,
  medium: Warning,
  success: ShieldCheck,
  primary: Lightning,
};

export default function AiInsightCard({
  endpoint = "/ai/assistant",
  title = "Assistant IA",
  limit = 3,
  className = "",
  compact = false,
}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(endpoint)
      .then(({ data }) => {
        if (!cancelled) setPayload(data);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  const items = (payload?.items || []).slice(0, limit);
  if (!loading && items.length === 0) return null;

  return (
    <section
      className={`rounded-2xl border border-primary/25 bg-primary/10 p-3 text-foreground shadow-[0_18px_60px_rgba(0,0,0,0.22)] ${className}`}
      data-testid="ai-insight-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Brain size={17} weight="fill" />
          </span>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{title}</div>
            {!compact && (
              <div className="text-[11px] text-muted-foreground">
                {payload?.headline || "Analyse intelligente en cours"}
              </div>
            )}
          </div>
        </div>
        <Badge tone="primary">IA</Badge>
      </div>

      <div className={`${compact ? "mt-2" : "mt-3"} space-y-2`}>
        {loading && (
          <div className="rounded-xl bg-white/5 px-3 py-2 text-xs text-muted-foreground">
            Analyse des donnees...
          </div>
        )}
        {!loading &&
          items.map((item) => {
            const Icon = iconMap[item.severity] || ShieldCheck;
            return (
              <div key={item.id} className="rounded-xl bg-black/15 px-3 py-2">
                <div className="flex items-start gap-2">
                  <Icon
                    size={15}
                    weight="fill"
                    className={
                      item.severity === "critical" || item.severity === "high"
                        ? "mt-0.5 shrink-0 text-destructive"
                        : item.severity === "warning" || item.severity === "medium"
                          ? "mt-0.5 shrink-0 text-[hsl(var(--warning))]"
                          : "mt-0.5 shrink-0 text-primary"
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate text-xs font-bold">{item.title}</div>
                      <Badge tone={toneMap[item.severity] || "default"} className="shrink-0">
                        {item.category || "info"}
                      </Badge>
                    </div>
                    <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{item.message}</div>
                    {item.action && item.href && (
                      <Link
                        to={item.href}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary"
                        data-testid={`ai-action-${item.id}`}
                      >
                        {item.action} <ArrowRight size={11} weight="bold" />
                      </Link>
                    )}
                    {item.action && !item.href && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                        {item.action} <ArrowRight size={11} weight="bold" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
}
